import Card from '../../../components/Card'
import React, { useMemo } from 'react'
import { StoredSpot } from '../../../types/Individualization'
import { v4 as uuid } from 'uuid'
import { useTranslation } from 'react-i18next'
import { useCustomizer } from '../../../contexts/CustomizerContext'
import TextElementInput from './TextElementInput'
import ImgElementInput from './ImageElementInput'
import Button from '../../../components/Button'

type CuttingMaskElementInputProps = {
    spot: StoredSpot
}

const CuttingMaskElementInput: React.FC<CuttingMaskElementInputProps> = ({
    spot,
}) => {
    const { t } = useTranslation(['customizer'])
    const { cuttingMaskElements, setCuttingMaskElements } = useCustomizer()

    const imageMaxReached = useMemo(() => {
        const elements = cuttingMaskElements.get(spot.id) ?? []
        return (
            spot.max_image_fields <=
            elements.filter((ele) => ele.type === 'image').length
        )
    }, [cuttingMaskElements, spot])

    const textMaxReached = useMemo(() => {
        const elements = cuttingMaskElements.get(spot.id) ?? []
        return (
            spot.max_text_fields <=
            elements.filter((ele) => ele.type === 'text').length
        )
    }, [cuttingMaskElements, spot])

    const addElement = (type: 'text' | 'image') => {
        if (
            (!textMaxReached && type === 'text') ||
            (!imageMaxReached && type === 'image')
        ) {
            setCuttingMaskElements((prev) => {
                const old = prev.get(spot.id) ?? []
                return new Map(
                    prev.set(spot.id, [...old, { uid: uuid(), type }])
                )
            })
        }
    }

    const removeElement = (uid: string) => {
        setCuttingMaskElements((prev) => {
            const old = prev.get(spot.id) ?? []
            return new Map(
                prev.set(
                    spot.id,
                    old.filter((e) => e.uid !== uid)
                )
            )
        })
    }

    return (
        <Card key={spot.id}>
            <Card.Header>
                {spot.name}
            </Card.Header>
            <>
                {spot.hint && <p dangerouslySetInnerHTML={{__html: spot.hint ?? ''}}/>}
                {(!textMaxReached || !imageMaxReached) && (
                    <div
                        style={{
                            gap: '1rem',
                            marginBottom: cuttingMaskElements.get(spot.id)
                                ?.length
                                ? '1rem'
                                : '0',
                        }}
                        className={'zr-d-flex'}
                    >
                        {!textMaxReached && (
                            <Button
                                hierarchy={'secondary'}
                                type={'button'}
                                text={t('add_text_element')}
                                fn={() => addElement('text')}
                                style={{
                                    padding: '0.5rem 0.5rem',
                                    borderWidth: '2px',
                                    minWidth: 'auto',
                                    width: '50%',
                                }}
                            />
                        )}
                        {!imageMaxReached && (
                            <Button
                                hierarchy={'secondary'}
                                type={'button'}
                                text={t('add_image_element')}
                                fn={() => addElement('image')}
                                style={{
                                    padding: '0.5rem 0.5rem',
                                    borderWidth: '2px',
                                    minWidth: 'auto',
                                    width: '50%',
                                }}
                            />
                        )}
                    </div>
                )}
                <div
                    className={'zr-d-flex zr-flex-column-reverse'}
                    style={{ gap: '1rem' }}
                >
                    {cuttingMaskElements.get(spot.id)?.map((ele) => {
                        return ele.type === 'text' ? (
                            <TextElementInput
                                key={ele.uid}
                                spot={spot}
                                uid={ele.uid}
                                onDelete={removeElement}
                                showHeading={false}
                                autoFocus={true}
                            />
                        ) : (
                            <ImgElementInput
                                key={ele.uid}
                                spot={spot}
                                uid={ele.uid}
                                onDelete={removeElement}
                                showHeading={false}
                            />
                        )
                    })}
                </div>
            </>
        </Card>
    )
}

export default CuttingMaskElementInput
